import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { CommunityMember } from '../../../../shared/types';

interface CommunityMemberFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  member: CommunityMember | null;
  onSave: (memberId: string | null, data: Partial<CommunityMember>) => Promise<void>;
  allMembers: CommunityMember[];
}

export function CommunityMemberFormDialog({
  open,
  onOpenChange,
  mode,
  member,
  onSave,
  allMembers,
}: CommunityMemberFormDialogProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Partial<CommunityMember>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Get unique values for autocomplete
  const uniqueOptions = useMemo(() => ({
    countries: [...new Set(allMembers.map(m => m.country))].filter(Boolean).sort(),
    clapbases: [...new Set(allMembers.map(m => m.clapbase))].filter(Boolean).sort(),
    roles: [...new Set(allMembers.map(m => m.role))].filter(Boolean).sort(),
    levels: [...new Set(allMembers.map(m => m.level).filter(Boolean))].sort(),
    teams: [...new Set(allMembers.map(m => m.team).filter(Boolean))].sort(),
    areas: [...new Set(allMembers.map(m => m.area).filter(Boolean))].sort(),
    managers: [...new Set([
      ...allMembers.map(m => m.peopleManager),
      ...allMembers.map(m => m.n1Manager),
      ...allMembers.map(m => m.responsableArea),
    ])].filter(Boolean).sort(),
  }), [allMembers]);

  useEffect(() => {
    if (mode === 'create') {
      setFormData({
        status: 'Activo',
      });
    } else if (member) {
      setFormData({
        employeeId: member.employeeId,
        name: member.name,
        lastName: member.lastName,
        email: member.email,
        country: member.country,
        clapbase: member.clapbase,
        role: member.role,
        level: member.level,
        team: member.team,
        area: member.area,
        peopleManager: member.peopleManager,
        n1Manager: member.n1Manager,
        responsableArea: member.responsableArea,
        status: member.status,
      });
    }
  }, [mode, member]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(member?.memberId || null, formData);
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving member:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? t('users.community.form.title_create') : t('users.community.form.title_edit', { id: member?.employeeId })}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Row 0: Employee ID (only for create) */}
          {mode === 'create' && (
            <div className="space-y-2">
              <Label htmlFor="employeeId">{t('users.community.form.employee_id')}</Label>
              <Input
                id="employeeId"
                type="number"
                value={formData.employeeId || ''}
                onChange={(e) => updateField('employeeId', e.target.value ? parseInt(e.target.value) : '')}
              />
            </div>
          )}

          {/* Row 1: Name and Last Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('users.community.form.name')}</Label>
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">{t('users.community.form.last_name')}</Label>
              <Input
                id="lastName"
                value={formData.lastName || ''}
                onChange={(e) => updateField('lastName', e.target.value)}
              />
            </div>
          </div>

          {/* Row 2: Email */}
          <div className="space-y-2">
            <Label htmlFor="email">{t('users.community.form.email')}</Label>
            <Input
              id="email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => updateField('email', e.target.value)}
            />
          </div>

          {/* Row 3: Country and Clapbase */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="country">{t('users.community.form.country')}</Label>
              <Input
                id="country"
                list="countries-list"
                value={formData.country || ''}
                onChange={(e) => updateField('country', e.target.value)}
              />
              <datalist id="countries-list">
                {uniqueOptions.countries.map((country) => (
                  <option key={country} value={country} />
                ))}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label htmlFor="clapbase">{t('users.community.form.clapbase')}</Label>
              <Input
                id="clapbase"
                list="clapbases-list"
                value={formData.clapbase || ''}
                onChange={(e) => updateField('clapbase', e.target.value)}
              />
              <datalist id="clapbases-list">
                {uniqueOptions.clapbases.map((clapbase) => (
                  <option key={clapbase} value={clapbase} />
                ))}
              </datalist>
            </div>
          </div>

          {/* Row 4: Role */}
          <div className="space-y-2">
            <Label htmlFor="role">{t('users.community.form.role')}</Label>
            <Input
              id="role"
              list="roles-list"
              value={formData.role || ''}
              onChange={(e) => updateField('role', e.target.value)}
            />
            <datalist id="roles-list">
              {uniqueOptions.roles.map((role) => (
                <option key={role} value={role} />
              ))}
            </datalist>
          </div>

          {/* Row 5: Level, Team, Area */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="level">{t('users.community.form.level')}</Label>
              <Input
                id="level"
                list="levels-list"
                value={formData.level || ''}
                onChange={(e) => updateField('level', e.target.value)}
              />
              <datalist id="levels-list">
                {uniqueOptions.levels.map((level) => (
                  <option key={level} value={level} />
                ))}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label htmlFor="team">{t('users.community.form.team')}</Label>
              <Input
                id="team"
                list="teams-list"
                value={formData.team || ''}
                onChange={(e) => updateField('team', e.target.value)}
              />
              <datalist id="teams-list">
                {uniqueOptions.teams.map((team) => (
                  <option key={team} value={team} />
                ))}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label htmlFor="area">{t('users.community.form.area')}</Label>
              <Input
                id="area"
                list="areas-list"
                value={formData.area || ''}
                onChange={(e) => updateField('area', e.target.value)}
              />
              <datalist id="areas-list">
                {uniqueOptions.areas.map((area) => (
                  <option key={area} value={area} />
                ))}
              </datalist>
            </div>
          </div>

          {/* Row 6: Managers */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="peopleManager">{t('users.community.form.people_manager')}</Label>
              <Input
                id="peopleManager"
                list="managers-list"
                value={formData.peopleManager || ''}
                onChange={(e) => updateField('peopleManager', e.target.value)}
              />
              <datalist id="managers-list">
                {uniqueOptions.managers.map((manager) => (
                  <option key={manager} value={manager} />
                ))}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label htmlFor="n1Manager">{t('users.community.form.n1_manager')}</Label>
              <Input
                id="n1Manager"
                list="managers-list"
                value={formData.n1Manager || ''}
                onChange={(e) => updateField('n1Manager', e.target.value)}
              />
            </div>
          </div>

          {/* Row 7: Responsable Area */}
          <div className="space-y-2">
            <Label htmlFor="responsableArea">{t('users.community.form.responsable_area')}</Label>
            <Input
              id="responsableArea"
              list="managers-list"
              value={formData.responsableArea || ''}
              onChange={(e) => updateField('responsableArea', e.target.value)}
            />
          </div>

          {/* Row 8: Status */}
          <div className="space-y-2">
            <Label htmlFor="status">{t('users.community.form.status')}</Label>
            <Select
              value={formData.status || 'Activo'}
              onValueChange={(value) => updateField('status', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Activo">Activo</SelectItem>
                <SelectItem value="Inactivo">Inactivo</SelectItem>
                <SelectItem value="Baja">Baja</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            {t('users.community.form.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? t('users.community.form.saving') : t('users.community.form.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
